import { useReselector } from '@/utils/sharedHooks'
import { convertUtcToLocalDate, DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { selectCurrentAnnualTaxDetails } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import { TypographyStyles } from '@/styles'
import { Link, List, Text } from '@/ui'

export const Terms1040 = ({ currentTaxYear }: { currentTaxYear: string }) => {
  const activeDetails = useReselector(selectCurrentAnnualTaxDetails)

  if (!activeDetails) return null

  const form1040IRSDueDate = convertUtcToLocalDate(
    activeDetails.form_1040_irs_due_date
  )?.endOf('day')

  const form1040ExtensionRequestDueDate = convertUtcToLocalDate(
    activeDetails.form_1040_extension_survey_due_date
  )?.endOf('day')

  return (
    <div className="flex flex-col space-y-6">
      <Text variant={TypographyStyles.H2} className="w-full">
        1. You must file an extension request
      </Text>

      <Text className="min-h-fit">
        In order to ensure that Heard has enough time to get all of your{' '}
        {currentTaxYear} books in order, we will require that you file an
        extension with the IRS as a precautionary measure to give you more time
        to file your return, if needed. If you&apos;re able to provide Heard
        with all of the necessary information quickly, it is possible that you
        can still file before the normal{' '}
        {form1040IRSDueDate?.toFormat(DATE_FORMATS_LUXON.MONTH_DAY_LONG)}{' '}
        deadline - but the extension request will ensure that you are protected
        from failure to file penalties just in case.
      </Text>

      <Text variant={TypographyStyles.H2} className="w-full">
        2. You must complete the extension request survey by{' '}
        <b>
          {form1040ExtensionRequestDueDate?.toFormat(
            DATE_FORMATS_LUXON.DISPLAY_LONG_SHORT_DAY_FULL
          )}
        </b>
      </Text>

      <Text className="w-full min-h-fit">
        This first step is necessary to file an extension, and to avoid
        &quot;failure to file&quot; penalties.
      </Text>

      <Text variant={TypographyStyles.H2} className="w-full">
        3. You may still be responsible for some interest and penalties.
      </Text>

      <Text className="w-full min-h-fit">
        We will help you file an extension to avoid the &quot;failure to
        file&quot; penalty, but a &quot;failure to pay&quot; penalty may still
        apply if you underpaid your {currentTaxYear} quarterly tax payments, or
        do not pay the balance owed by{' '}
        {form1040IRSDueDate?.toFormat(
          DATE_FORMATS_LUXON.DISPLAY_LONG_SHORT_DAY_FULL
        )}
        .{' '}
        <Link
          href={
            'https://support.joinheard.com/hc/en-us/articles/4416196188439-What-do-you-mean-by-estimate-Can-I-pay-more-or-less-than-the-estimate-you-gave-me'
          }
          newPage
        >
          Learn more
        </Link>
      </Text>

      <Text variant={TypographyStyles.H2} className="w-full">
        4. After filing an extension, we recommend you complete all of the below
        ASAP
      </Text>

      <div className="w-full min-h-fit">
        <List bulleted>
          <List.Item>
            <Text>Upload documents for catch up bookkeeping</Text>
          </List.Item>

          <List.Item>
            <Text>Completing your end-of-year accounting wrap up</Text>
          </List.Item>

          <List.Item>
            <Text>
              Complete your tax checklist to be connected to a tax preparer
            </Text>
          </List.Item>
        </List>
      </div>
    </div>
  )
}
